// src/data/lawsuits.js
export const lawsuits = [
  // ───────────────────────────────────────────────
  // 1) Dangerous & Defective Products
  // ───────────────────────────────────────────────
  {
    slug: "baby-formula-nec",
    title: "Baby Formula NEC Lawsuits",
    summary: "NEC injuries in premature infants.",
    heroImg: "/tort/nec3.jpg",
    highlights: ["No upfront fees", "100% confidential"],
    gallery: ["/tort/nec1.jpg", "/tort/nec2.jpg"],
    eligibility: [
      "Baby born premature (preterm/NICU stay)",
      "Fed cow’s-milk formula or fortifier (e.g., Similac/Enfamil)",
      "Diagnosed with NEC or required intestinal surgery",
      "Medical records available (NICU notes, discharge summary)",
      "Parent/guardian willing to discuss timeline and feeding history",
      "No attorney currently handling this specific claim",
    ],
    overview: [
      "• How we can help: Our network coordinates record requests, reviews NICU timelines and feeding histories, and advises families whether a civil claim may help cover past and future care.",
      "• Understanding NEC: Necrotizing enterocolitis is a serious intestinal disease seen most in premature infants. Allegations focus on whether cow’s-milk-based products increased risk for vulnerable babies.",
      "• Common fact patterns: Early NICU fortification with cow’s-milk products; abdominal distention; blood in stool; rapid clinical decline; surgery or prolonged NICU stay; long-term complications.",
      "• Evidence checklist: NICU admission and progress notes, operative/pathology reports, feeding logs, brand documentation when available, growth charts, and follow-up pediatric surgery/ GI notes.",
      "• Potential compensation: Medical expenses, long-term therapy/equipment, home modifications, and non-economic harms. Contingency representation means no fees unless a recovery is made.",
      "• Timing matters: Medical institutions archive records on set schedules. Starting now helps preserve documents and witness recollections.",
      "• Process overview: Free review → records request → legal evaluation → filing if appropriate → negotiation or litigation. Families remain in control of decisions.",
      "• Privacy and dignity: We handle intake with empathy and keep your information confidential.",
    ],
    disqualifiers: [
      "No NEC diagnosis or related surgery/treatment",
      "No formula/fortifier exposure",
    ],
    faqs: [
      {
        q: "Do we need the exact brand?",
        a: "Brand info helps but isn’t required to start. NICU logs often show what was used.",
      },
      {
        q: "Will this affect ongoing care?",
        a: "No. Legal review is separate from your child’s medical care and can run in parallel.",
      },
      {
        q: "How long does a case take?",
        a: "Timelines vary by records and court schedules. We aim to move quickly once documents arrive.",
      },
      {
        q: "Any upfront cost?",
        a: "No. We only get paid if we obtain a recovery.",
      },
    ],
  },

  // ───────────────────────────────────────────────
  // 2) Exposure to Toxic Substances
  // ───────────────────────────────────────────────
  {
    slug: "roundup-cancer",
    title: "RoundUp Cancer Lawsuits",
    summary: "Exposure to glyphosate linked to cancers.",
    heroImg: "/tort/roundup4.jpg",
    highlights: ["No fees unless we win", "Nationwide network"],
    gallery: ["/tort/roundup1.jpg", "/tort/roundup5.jpg"],
    eligibility: [
      "Regular RoundUp (glyphosate) use at home, work, or property",
      "Cancer diagnosis (e.g., non-Hodgkin’s lymphoma) or medical evaluation",
      "Doctor notes or oncology records available",
      "Work or purchase history helpful (not required to start)",
      "No attorney currently handling this claim",
      "Within applicable statute of limitations",
    ],
    overview: [
      "• How we can help: We collect work/home use details and medical records, evaluate cancer subtypes, and guide next steps on a contingency basis.",
      "• Understanding exposure: RoundUp is a glyphosate herbicide used in landscaping, farming, and residential settings. Allegations focus on whether long-term use increased certain cancer risks.",
      "• Typical evidence: Treatment summaries, pathology reports, physician letters, employment or landscaping logs, and receipts or photos showing product use.",
      "• Medical focus: Non-Hodgkin’s lymphoma subtypes are frequently reviewed; other diagnoses are considered case-by-case with physician support.",
      "• Compensation targets: Medical bills, lost income, and non-economic harms. Claims may resolve by settlement or proceed through litigation.",
      "• Deadlines exist: Each state sets time limits for filing. Starting the review preserves options and helps secure documentation.",
      "• Your role: Share your use timeline and care history; we’ll handle record requests and legal filings if you choose to proceed.",
      "• Confidential review: Your details remain private and there are no upfront fees.",
    ],
    disqualifiers: [
      "No documented use/exposure",
      "No diagnosis or medical evaluation",
    ],
    faqs: [
      {
        q: "What if I bought RoundUp years ago?",
        a: "Approximate dates are fine. Old receipts/photos can help but aren’t required to start.",
      },
      {
        q: "Does brief use qualify?",
        a: "We review all exposures, but long-term or occupational use is often more significant.",
      },
      {
        q: "Will I need to testify?",
        a: "Many claims resolve without testimony, but we’ll prepare you if needed.",
      },
      { q: "Upfront cost?", a: "None—cases are handled on contingency." },
    ],
  },

  {
    slug: "hair-relaxer-cancer",
    title: "Chemical Hair Relaxers and Straighteners Cancer",
    summary: "Use tied to increased cancer risk.",
    heroImg: "/tort/hair-relaxer1.jpg",
    highlights: ["No upfront fees", "100% confidential"],
    gallery: ["/tort/hair-relaxer2.jpg", "/tort/hair-relaxer.jpg"],
    eligibility: [
      "Long-term use of chemical relaxers/straighteners",
      "Uterine, ovarian, or endometrial cancer (or medical evaluation)",
      "OB-GYN/oncology records available",
      "Cosmetic use timeline (brands/salons/home) helpful",
      "No attorney currently handling the matter",
      "Within applicable filing deadlines",
    ],
    overview: [
      "• What’s alleged: Lawsuits claim certain chemical relaxers may increase the risk of specific cancers when used over many years.",
      "• How we help: We review use history, collect medical records, and evaluate eligibility in a discreet, no-cost consultation.",
      "• Evidence that helps: Pathology reports, surgery or treatment notes, and any proof of long-term product use (receipts, salon history, photos).",
      "• Scope of damages: Medical costs, time off work, and non-economic harms may be recoverable depending on the case.",
      "• Timing: Many states have strict deadlines that can be extended or tolled in limited circumstances—reach out soon.",
      "• Process: Intake → records → legal review → filing if appropriate → negotiation or litigation. You decide whether to move forward.",
      "• Privacy first: Sensitive health details are kept confidential.",
      "• No risk: Contingency fee—no recovery, no fee.",
    ],
    disqualifiers: [
      "No relevant diagnosis or medical evaluation",
      "No history of product use",
    ],
    faqs: [
      {
        q: "Do brand names matter?",
        a: "Brand info helps but isn’t required to begin—share what you remember.",
      },
      {
        q: "What if the cancer was years ago?",
        a: "Still contact us. We’ll check deadlines and what records exist.",
      },
      {
        q: "Will this affect my treatment?",
        a: "No. Legal review is independent from your medical care.",
      },
      {
        q: "Any cost to request records?",
        a: "We handle standard fees for medical records during case evaluation.",
      },
    ],
  },

  {
    slug: "paraquat-parkinsons",
    title: "Paraquat Herbicide Lawsuits",
    summary: "Herbicide exposure linked to Parkinson’s.",
    heroImg: "/tort/paraquat.jpg",
    highlights: ["Nationwide review", "Confidential & fast"],
    gallery: ["/tort/paraquat1.jpg", "/tort/paraquat2.jpg"],
    eligibility: [
      "Worked with or near Paraquat (farms/groundskeeping)",
      "Parkinson’s diagnosis or neurologist evaluation",
      "Exposure years align with symptom onset",
      "Employment or location history available",
      "No attorney currently handling this claim",
      "Within applicable deadlines",
    ],
    overview: [
      "• Background: Paraquat is a restricted herbicide. Research has associated certain exposures with increased Parkinson’s risk.",
      "• Evidence often reviewed: Neurology notes, PD medications history, occupational records, and affidavits about spraying proximity.",
      "• Exposure routes: Mixing or spraying, equipment maintenance, or living near application zones; each situation is evaluated on facts.",
      "• What compensation may include: Medical care, mobility aids, lost wages, and non-economic harms tied to long-term disability.",
      "• Why timing matters: Witnesses retire, and employment or custom-application logs can be harder to locate as time passes.",
      "• Process and support: We coordinate records and connect you with attorneys experienced in ag-exposure cases—no fees unless a recovery is made.",
      "• Family involvement: Caregivers can help reconstruct exposure timelines and treatment milestones.",
      "• Confidential evaluation: Your information is protected throughout.",
    ],
    disqualifiers: [
      "No exposure history",
      "No Parkinson’s diagnosis/evaluation",
    ],
    faqs: [
      {
        q: "Do I need exact dates of exposure?",
        a: "Approximate seasons/years and employer names are a great start.",
      },
      {
        q: "What if I moved states?",
        a: "We review multi-state timelines and advise on where filing may be appropriate.",
      },
      {
        q: "Will I have to travel?",
        a: "Most case work is handled remotely; if travel is required we’ll discuss options.",
      },
      { q: "Upfront cost?", a: "None—contingency only." },
    ],
  },

  {
    slug: "asbestos-lung-cancer",
    title: "Lung Cancer Asbestos Lawsuit",
    summary: "Asbestos exposure (lung cancer only).",
    heroImg: "/tort/asbestos1.jpg",
    highlights: ["No upfront fees", "Decades of records handled"],
    gallery: ["/tort/asbestos2.jpg", "/tort/asbestos3.jpg"],
    eligibility: [
      "Occupational or secondary asbestos exposure (e.g., construction, shipyards)",
      "Lung cancer diagnosis (smoking history does not automatically disqualify)",
      "Work history and pathology/imaging available",
      "Family members exposed through contaminated clothing considered",
      "No attorney currently handling this claim",
      "Within statute of limitations",
    ],
    overview: [
      "• Understanding exposure: Asbestos fibers were used widely in insulation, piping, brakes, and industrial materials for decades.",
      "• Why cases proceed: Even with a smoking history, asbestos can be a substantial factor in lung cancer. Worksite records often matter.",
      "• Evidence that helps: Employment histories, union cards, crew lists, co-worker statements, x-rays/CT scans, and pathology reports.",
      "• Types of compensation: Medical costs, lost income, and non-economic harms; estates can continue claims when applicable.",
      "• Secondary exposures: Family members exposed via dusty work clothing may also be reviewed on a case-by-case basis.",
      "• Deadlines & trusts: Some compensation may flow from asbestos bankruptcy trusts; others are filed in court—timing is critical.",
      "• How we help: We locate historical records and align them with medical proof—no fees unless a recovery is made.",
      "• Confidential review: Start with a brief, private call or form.",
    ],
    disqualifiers: ["No exposure", "No lung cancer diagnosis"],
    faqs: [
      {
        q: "Does smoking bar a claim?",
        a: "No. It’s a factor, but not an automatic bar. We review the whole exposure picture.",
      },
      {
        q: "What if the company is closed?",
        a: "Many records exist; some claims go through asbestos trusts or successor entities.",
      },
      {
        q: "How long do cases take?",
        a: "Varies by records and court. We move promptly after intake.",
      },
      {
        q: "Will my family need to help?",
        a: "Often yes—spouses or adult children can assist with history and signatures.",
      },
    ],
  },

  {
    slug: "pfas-water",
    title: "PFAS Water Contamination Lawsuits",
    summary: "‘Forever chemicals’ in water linked to health risks.",
    heroImg: "/tort/pfas1.jpg",
    highlights: ["No upfront fees", "Fast, confidential review"],
    gallery: ["/tort/pfas4.jpg", "/tort/pfas3.jpg"],
    eligibility: [
      "Municipal or private well water used for years",
      "Diagnosis consistent with PFAS exposure (e.g., kidney/testicular cancer, thyroid disease)",
      "Address history near impacted supplies (utilities or well logs)",
      "Basic medical records available",
      "No attorney on this specific claim",
      "Within deadlines for your state",
    ],
    overview: [
      "• What PFAS are: Persistent chemicals used in coatings/foams that can travel into groundwater and drinking water.",
      "• Allegations: Manufacturers and other entities are accused of failing to adequately warn or prevent contamination.",
      "• What we review: Address timelines, water source, testing data if available, and medical documentation of related conditions.",
      "• Proof examples: Utility bills, well records, local notices, registry letters, and physician notes linking conditions to exposure.",
      "• Possible recovery: Medical costs, monitoring, diminished quality of life, and other losses depending on facts.",
      "• Why act now: Utility and lab records can be time-limited; prompt intake preserves access to documentation.",
      "• Our role: We coordinate records and connect you to counsel pursuing PFAS claims—no fees unless a recovery results.",
      "• Privacy & respect: Your information stays confidential throughout the review.",
    ],
    disqualifiers: ["No relevant diagnosis", "No PFAS-linked exposure"],
    faqs: [
      {
        q: "Do I need lab tests?",
        a: "Not to start. Share any notices or local testing you received; we’ll advise from there.",
      },
      {
        q: "What if I moved?",
        a: "Provide addresses and years; we map exposure across locations.",
      },
      {
        q: "Can renters qualify?",
        a: "Yes—exposure depends on water source, not property ownership.",
      },
      { q: "Any cost up front?", a: "No, contingency only." },
    ],
  },

  {
    slug: "la-wildfire",
    title: "Los Angeles Wildfire",
    summary: "Property loss and smoke injuries.",
    heroImg: "/tort/wildfire1.jpg",
    highlights: ["No upfront fees", "Property & smoke damage"],
    gallery: ["/tort/wildfire2.jpg", "/tort/wildfire3.jpg"],
    eligibility: [
      "Home or business in impacted burn/smoke area and timeframe",
      "Structural loss, smoke infiltration, or extended evacuation expenses",
      "Receipts, photos, adjuster reports, or contractor estimates available",
      "Health impacts from smoke documented by a clinician (when applicable)",
      "No attorney currently managing your wildfire claim",
      "Claim within applicable deadlines",
    ],
    overview: [
      "• What claims address: Repair/rebuild costs, smoke remediation, damaged contents, additional living expenses (ALE), and lost business income.",
      "• Evidence that helps: Photos, adjuster reports, air quality tests, contractor bids, and receipts for temporary housing or cleaning.",
      "• Insurance coordination: Some claims supplement or challenge insurer decisions; others target third parties where appropriate.",
      "• For renters: ALE and contents claims may apply even without property ownership—keep your lease and receipts.",
      "• Health impacts: Document any smoke-related visits; keep pharmacy receipts for inhalers or medications.",
      "• Timing: Short filing windows may apply. Starting now preserves invoices and witness recollections.",
      "• Process support: We’ll help assemble a damage file and pursue the appropriate recovery path.",
      "• No upfront fees: Contingency representation.",
    ],
    disqualifiers: ["No wildfire-related loss or expense"],
    faqs: [
      {
        q: "Do I need an insurer denial?",
        a: "Not always. Bring whatever you have; we’ll review options.",
      },
      {
        q: "Will I have to sue neighbors?",
        a: "Most cases target insurers or other entities—not neighbors.",
      },
      {
        q: "What if I already repaired?",
        a: "Save invoices/photos; you may still seek reimbursement.",
      },
      {
        q: "Can businesses qualify?",
        a: "Yes—lost income and repair costs can be reviewed.",
      },
    ],
  },

  {
  slug: "silicosis",
  title: "Silicosis Lawsuit",
  summary: "Lung disease and respiratory complications caused by silica dust exposure.",
  heroImg: "/tort/silicosis5.jpg",

  highlights: [
    "Free case evaluation",
    "Occupational exposure claims"
  ],

  gallery: [
    "/tort/silicosis4.jpg",
    "/tort/silicosis2.jpg"
  ],

  eligibility: [
    "Exposure to silica dust",
    "Diagnosed with silicosis or respiratory disease",
    "Work history involving stone, quartz, or concrete",
    "No attorney currently representing this claim"
  ],

  overview: [
    "• Placeholder content for Silicosis lawsuit.",
    "• Full SEO content will be added later.",
    "• Claims involve occupational silica exposure.",
    "• Medical and employment records may support eligibility."
  ],

  disqualifiers: [
    "No silica exposure",
    "No respiratory diagnosis"
  ],

  faqs: [
    {
      q: "What industries are commonly affected?",
      a: "Construction, mining, stone fabrication, and countertop work are commonly associated with silica exposure."
    },
    {
      q: "Do I need medical proof?",
      a: "Basic medical records or diagnosis details can help begin the review process."
    }
  ],
},

  // ───────────────────────────────────────────────
  // 3) Defective Drugs & Medical Devices
  // ───────────────────────────────────────────────
  {
    slug: "oxbryta",
    title: "Oxbryta Lawsuits",
    summary: "Safety concerns under review.",
    heroImg: "/tort/oxbryta3.jpg",
    highlights: ["No upfront fees", "Confidential review"],
    gallery: ["/tort/oxbryta1.jpg", "/tort/oxbryta2.jpg"],
    eligibility: [
      "Oxbryta prescribed and used",
      "Serious adverse effects or hospitalization",
      "Medical records available (ER notes, labs, physician letters)",
      "No attorney currently handling this claim",
      "Claim fits within state deadlines",
      "Willing to discuss timeline and prescribers",
    ],
    overview: [
      "• What’s at issue: Reported adverse events have prompted reviews of potential safety risks and warnings.",
      "• Evidence we gather: Pharmacy fills, prescriber notes, ER summaries, and any imaging or lab results tied to the reaction.",
      "• Medical focus: Symptom onset relative to first dose, dose changes, and differential diagnosis documented by your physician.",
      "• Recovery aims: Medical costs, lost time, and non-economic harms depending on the facts.",
      "• Process clarity: Free review → records → legal evaluation → filing if appropriate → resolution by settlement or litigation.",
      "• Deadlines apply: States restrict how long you can bring claims; faster intakes protect options.",
      "• Patient advocacy: We coordinate record requests so you can focus on health.",
      "• No costs up front: Contingency-based representation.",
    ],
    disqualifiers: ["No Oxbryta use", "No documented adverse effects"],
    faqs: [
      {
        q: "What if I changed doctors?",
        a: "List all prescribers and facilities; we’ll request records from each.",
      },
      {
        q: "Do I need the pill bottle?",
        a: "Bottles help but aren’t required. Pharmacy records are often enough.",
      },
      {
        q: "Will this affect my current treatment?",
        a: "No—your medical care remains independent.",
      },
      { q: "Any fee to start?", a: "No. We only get paid if you recover." },
    ],
  },

  {
    slug: "bard-powerport",
    title: "Bard PowerPort Catheter Lawsuits",
    summary: "Catheter fracture/migration claims.",
    heroImg: "/tort/port-catheter1.jpg",
    highlights: ["No upfront fees", "Device complication support"],
    gallery: ["/tort/port-catheter4.jpg", "/tort/port-catheter2.jpg"],
    eligibility: [
      "PowerPort (or similar) implanted",
      "Complication: fracture, migration, infection, thrombosis, device failure",
      "Removal/revision surgery or hospitalization",
      "Implant card, operative notes, or ER documentation available",
      "No current attorney for this claim",
      "Within filing deadlines",
    ],
    overview: [
      "• Allegations: Some implanted ports are linked to complications like fracture, migration, clotting, or infection requiring removal.",
      "• Records that help: Operative reports, device stickers, interventional radiology notes, blood culture results, and discharge summaries.",
      "• Injury patterns: Device breakage, embolization, bloodstream infection, or mechanical failure—each evaluated on medical proof.",
      "• Damages: Medical bills, additional procedures, time off work, and pain/suffering may be considered.",
      "• The path forward: We gather records and consult device experts as needed. If eligible, filing follows.",
      "• Hospital coordination: We can request records directly—sign releases once and we do the rest.",
      "• Confidential, contingency-based: No upfront fees; you remain in control.",
      "• Questions welcome: We’ll explain timelines and expected steps.",
    ],
    disqualifiers: ["No implanted port", "No complication/treatment"],
    faqs: [
      {
        q: "I lost my implant card—OK?",
        a: "Yes. Operative notes and imaging typically document the device.",
      },
      {
        q: "Device removed already?",
        a: "Keep removal records—those are often central to evaluation.",
      },
      {
        q: "Do I need to stop treatment?",
        a: "Never change care solely for a legal claim—follow medical advice.",
      },
      {
        q: "How long does it take?",
        a: "Varies; we move promptly once records arrive.",
      },
    ],
  },

  {
    slug: "hernia-mesh",
    title: "Hernia Mesh Lawsuits",
    summary: "Failure, pain, and revision surgery.",
    heroImg: "/tort/hernia-mesh1.jpg",
    highlights: ["No upfront fees", "Nationwide support"],
    gallery: ["/tort/hernia-mesh.jpg", "/tort/hernia-mesh.jpg"],
    eligibility: [
      "Hernia repair with mesh implant",
      "Complications: pain, infection, obstruction, recurrence, adhesion, migration",
      "Revision or removal surgery (planned or completed)",
      "Operative/pathology records available",
      "No attorney currently handling this matter",
      "Within applicable deadlines",
    ],
    overview: [
      "• What’s alleged: Certain mesh products may fail or cause complications requiring additional surgeries or long-term care.",
      "• Records to gather: Original operative report, device sticker, imaging, revision surgery notes, and pathology as applicable.",
      "• Symptom timeline: When pain or obstruction started relative to repair can matter—share your post-op course.",
      "• Damages landscape: Medical and revision costs, time off work, and non-economic harms may be pursued.",
      "• Process: Free review → records → expert/legal evaluation → filing if appropriate → negotiation or litigation.",
      "• Practical tip: Don’t delay seeking medical care—treatment comes first and creates helpful documentation.",
      "• No up-front fees: We work on contingency.",
      "• Privacy: Your health details are handled confidentially.",
    ],
    disqualifiers: ["No mesh implant", "No complications"],
    faqs: [
      {
        q: "I don’t know the brand—problem?",
        a: "Not at all. Operative reports almost always list it.",
      },
      {
        q: "What if my revision is scheduled?",
        a: "That’s fine—save pre-op and post-op paperwork for your file.",
      },
      {
        q: "Do I need a primary doctor referral?",
        a: "No, but your treating surgeon’s notes are key evidence.",
      },
      { q: "Any fee to start?", a: "No—contingency only." },
    ],
  },

  {
    slug: "ozempic-wegovy",
    title: "Ozempic and Wegovy Lawsuits",
    summary: "Severe GI issues and complications.",
    heroImg: "/tort/ozempic1.jpg",
    highlights: ["No upfront fees", "Confidential"],
    gallery: ["/tort/ozempic2.jpg", "/tort/ozempic.jpg"],
    eligibility: [
      "Used Ozempic, Wegovy, or similar GLP-1 drug",
      "Serious GI events (e.g., gastroparesis), obstruction, pancreatitis, or gallbladder disease",
      "ER visit/hospitalization or specialist notes available",
      "No attorney handling this specific claim",
      "Within state deadlines",
      "Willing to discuss timeline and prescribers",
    ],
    overview: [
      "• Allegations: Some users report significant GI events potentially requiring ER care or procedures after GLP-1 therapy.",
      "• Evidence that helps: Pharmacy fills, prescribing notes, GI consults, imaging, and ER summaries tied to the event.",
      "• Injury scope: Severity, duration, and medical interventions drive damages; every case is individual.",
      "• Potential recovery: Medical bills, missed work, and non-economic harms depending on evidence.",
      "• Medical first: Never change medications solely for a claim—follow your physician’s advice.",
      "• Process clarity: Intake → records → case evaluation → filing if appropriate → settlement/litigation.",
      "• Timing: States impose deadlines; starting early preserves options.",
      "• No upfront fees: Contingency representation.",
    ],
    disqualifiers: ["No GLP-1 use", "No significant medical event"],
    faqs: [
      {
        q: "I switched brands—does it matter?",
        a: "List all GLP-1 meds and dates; we’ll request complete pharmacy records.",
      },
      {
        q: "What if I’m still on therapy?",
        a: "That’s your medical decision. We only review the evidence and advise on legal options.",
      },
      {
        q: "How long does review take?",
        a: "Depends on how quickly records arrive; we keep you updated.",
      },
      { q: "Upfront costs?", a: "None." },
    ],
  },

  {
    slug: "depo-provera",
    title: "Depo Provera Lawsuits",
    summary: "Potential long-term side effects.",
    heroImg: "/tort/depo2.jpg",
    highlights: ["No upfront fees", "Confidential review"],
    gallery: ["/tort/depo1.jpg", "/tort/depo3.jpg"],
    eligibility: [
      "Depo-Provera injections for contraception or therapy",
      "Long-term adverse effects (e.g., bone density loss, fractures)",
      "DXA scans, imaging, or clinician notes available",
      "No attorney on this matter",
      "Within applicable deadlines",
      "Willing to share timeline and providers",
    ],
    overview: [
      "• Background: Some users allege long-term harms after extended Depo-Provera use and seek help covering care.",
      "• Evidence: DXA bone density scans, fracture imaging, orthopedic notes, and prescribing histories.",
      "• Damages: Medical costs, therapy, mobility aids, and non-economic harms may be considered.",
      "• Medical guidance: Continue physician-directed care; legal review is separate from treatment.",
      "• Process: Free intake → records → legal evaluation → filing if appropriate → negotiation or litigation.",
      "• Deadlines: State rules limit claims periods—contact us soon to protect options.",
      "• Coordination: We handle record requests to reduce burden.",
      "• No upfront fees: Contingency only.",
    ],
    disqualifiers: ["No Depo use", "No significant adverse effects"],
    faqs: [
      {
        q: "What if I stopped years ago?",
        a: "Still reach out—older imaging and pharmacy logs can be obtained.",
      },
      {
        q: "Do I need bone scans?",
        a: "Helpful but not required to start; we’ll review what’s available.",
      },
      {
        q: "Will this affect my benefits insurance?",
        a: "No. The review is independent of coverage.",
      },
      { q: "Any cost to begin?", a: "No." },
    ],
  },

  {
    slug: "suboxone",
    title: "Suboxone Lawsuit",
    summary: "Tooth decay and oral injuries.",
    heroImg: "/tort/suboxone4.jpg",
    highlights: ["No upfront fees", "Dental records welcomed"],
    gallery: ["/tort/suboxone2.jpg", "/tort/suboxone.jpg"],
    eligibility: [
      "Used Suboxone film or tablets",
      "New or worsening dental injuries (decay, erosion, extractions, root canals)",
      "Dentist treatment notes, imaging, or billing available",
      "No attorney on this claim",
      "Within filing deadlines",
      "Willing to discuss timeline and treating providers",
    ],
    overview: [
      "• Allegations: Serious dental problems have been reported after Suboxone use, including decay and tooth loss.",
      "• Evidence that helps: Dental charts, x-rays, treatment plans, itemized invoices, and before/after photos where available.",
      "• Impact: Restoration costs, implants, and repeated procedures can be significant; claims aim to offset these burdens.",
      "• Process: Intake → records → legal review → filing if appropriate → negotiation or litigation.",
      "• Your role: Share a timeline of use and symptom onset; we’ll gather the documentary proof.",
      "• Privacy: Health and recovery-related details are kept confidential.",
      "• No risk: Contingency fee structure.",
      "• Questions welcome: We’ll explain expected steps and timelines.",
    ],
    disqualifiers: ["No Suboxone use", "No dental injuries"],
    faqs: [
      {
        q: "No x-rays available—okay?",
        a: "Yes. We can begin with dentist notes and invoices while imaging is requested.",
      },
      {
        q: "Still using Suboxone?",
        a: "Follow your doctor’s advice; we only review evidence and options.",
      },
      {
        q: "Do brands/forms matter?",
        a: "List what you used; pharmacy records will fill gaps.",
      },
      { q: "Any upfront fees?", a: "None." },
    ],
  },

  {
  slug: "dupixent",
  title: "Dupixent Lawsuit",
  summary: "Eye inflammation and severe reactions reported.",
  heroImg: "/tort/dupixent.jpg",
  highlights: [
  "No upfront costs — pay only if you win",
  "Medical records and doctor notes help strengthen your claim",
  "Fast confidential evaluation",
  "Dedicated mass-tort support team"],
  gallery: ["/tort/dupixent1.jpg","/tort/dupixent2.jpg"],

  eligibility: [
  "Used Dupixent (injections for eczema, asthma, or sinus conditions)",
  "Developed eye inflammation (conjunctivitis, keratitis, blepharitis)",
  "Vision changes, blurry vision, light sensitivity, or chronic dryness",
  "Facial swelling, rash, or systemic inflammatory reaction",
  "Doctor visit, prescription history, or medical event linked to symptoms",
  "No attorney currently representing this claim"
],

  overview: [
  "• Dupixent (dupilumab) is a biologic medication prescribed for eczema, asthma, and chronic sinus inflammation.",
  "• Reports have surfaced linking Dupixent to serious ocular complications, including long-term damage to the eye’s surface.",
  "• Commonly reported reactions include conjunctivitis, keratitis, and vision changes requiring urgent medical care.",
  "• Some users experience severe immune-related reactions, facial swelling, or long-lasting inflammation.",
  "• Lawsuits allege the manufacturer failed to adequately warn patients about the risk of permanent eye issues.",
  "• Potential compensation may include medical bills, specialist visits, lost income, and pain and suffering.",
  "• Strong claims typically include documentation such as ophthalmology reports and timelines of symptom onset.",
  "• Legal teams are actively reviewing Dupixent-related injury cases for individuals across the U.S."],

disqualifiers: [
  "No history of Dupixent use",
  "Symptoms unrelated to Dupixent or caused by another condition",
  "Already represented by an attorney for this claim"
],


  faqs: [
  {
    q: "What injuries are linked to the Dupixent lawsuits?",
    a: "Most claims involve eye inflammation (conjunctivitis, keratitis), vision changes, chronic dryness, eyelid swelling, and immune-related reactions."
  },
  {
    q: "Do I need medical proof?",
    a: "Basic evidence such as doctor notes, ophthalmology visits, eye-drop prescriptions, or treatment history is enough to start."
  },
  {
    q: "Is stopping Dupixent required?",
    a: "No. Never stop or change medication without consulting your prescribing doctor."
  },
  {
    q: "How much compensation can be expected?",
    a: "Values vary based on severity, medical expenses, long-term impact, and evidence. Attorneys evaluate each case individually."
  },
  {
    q: "Does it cost anything upfront?",
    a: "No. Cases are handled on a contingency basis, meaning you pay nothing unless compensation is recovered."
  }
]
},

  // ───────────────────────────────────────────────
  // 4) Injury & Disability
  // ───────────────────────────────────────────────
  {
    slug: "auto-accident-personal-injury",
    title: "Auto Accident and Personal Injury Lawsuits",
    summary: "Get help after crashes and injuries.",
    heroImg: "/tort/auto1.jpg",
    highlights: ["No upfront fees", "We handle insurers"],
    gallery: ["/tort/auto2.jpg", "/tort/auto3.jpg"],
    eligibility: [
      "Injury from vehicle crash or similar incident",
      "Another party likely at fault (police report or witness helpful)",
      "Medical treatment sought or recommended",
      "Photos/estimates or property damage documentation",
      "No attorney currently handling the case",
      "Within statute of limitations",
    ],
    overview: [
      "• Why legal help matters: Insurers are incentivized to minimize payouts; early representation protects claim value.",
      "• Evidence that wins cases: Scene photos, black-box data, repair estimates, medical notes, and wage loss documentation.",
      "• Injury patterns: Head/neck, back/spine, fractures, or soft-tissue injuries—all require documentation and follow-up.",
      "• Recovery categories: Medical bills, lost wages, property damage, and pain/suffering depending on the facts.",
      "• Process: Claim notice → treatment → demand → negotiation → litigation if needed. You decide when to settle.",
      "• Medical first: Follow clinician advice—gaps in care hurt claims and your health.",
      "• Timing: States impose strict filing windows; start now to preserve evidence.",
      "• No upfront fees: Contingency representation.",
    ],
    disqualifiers: ["No injury", "Solely at fault"],
    faqs: [
      {
        q: "Do I speak to the other insurer?",
        a: "Let counsel handle communications to avoid harmful statements.",
      },
      {
        q: "My car is totaled—can you help?",
        a: "Yes. Property claims are often handled alongside injury claims.",
      },
      {
        q: "What if I had prior injuries?",
        a: "Disclose them; we focus on new aggravation or worsening.",
      },
      { q: "Fee to start?", a: "No." },
    ],
  },

  {
    slug: "ssdi-applications",
    title: "Social Security Disability Benefits (SSDI) Applications",
    summary: "Help filing for disability benefits.",
    heroImg: "/tort/ssdi2.jpg",
    highlights: ["Fast intake", "Appeals help"],
    gallery: ["/tort/ssdi1.jpg", "/tort/ssdi.jpg"],
    eligibility: [
      "Unable to work for 12+ months (or expected)",
      "Medical documentation supports limitations",
      "Sufficient work credits for SSDI (we’ll check)",
      "No representative currently handling claim",
      "U.S. residency and ID documentation available",
      "Willing to follow medical advice and keep appointments",
    ],
    overview: [
      "• Why claims are denied: Incomplete forms, missing records, and unclear functional limits are common problems we solve.",
      "• Evidence that helps: Doctor opinions on sitting/standing limits, RFC forms, imaging, therapy notes, and medication lists.",
      "• The SSDI process: Initial filing → possible reconsideration → ALJ hearing → Appeals Council or federal court if needed.",
      "• Work credits & SGA: We explain earnings rules and whether your work history meets credits for SSDI.",
      "• Medical continuity: Keep appointments and follow prescribed treatment to show ongoing disability.",
      "• Timelines: Appeals have strict deadlines. We calendar them and keep you on track.",
      "• Fees: Governed by SSA; paid only if you win and usually from backpay—no upfront charge.",
      "• Respect & clarity: We make a complicated system manageable.",
    ],
    disqualifiers: [
      "Currently working at substantial gainful levels",
      "No medical documentation",
    ],
    faqs: [
      {
        q: "Can I work part-time?",
        a: "Sometimes. Earnings must stay under SGA limits; we’ll advise.",
      },
      {
        q: "How long does this take?",
        a: "Varies by state and backlog; we push to keep it moving.",
      },
      {
        q: "What’s SSI vs SSDI?",
        a: "SSI is need-based; SSDI uses work credits. We check both automatically.",
      },
      { q: "Upfront fee?", a: "No." },
    ],
  },

  {
    slug: "birth-injury",
    title: "Birth Injury Lawyers",
    summary: "Negligence causing birth injuries.",
    heroImg: "/tort/birth.jpg",
    highlights: ["Compassionate review", "No upfront fees"],
    gallery: ["/tort/birth1.jpg", "/tort/birth2.jpg"],
    eligibility: [
      "Injury to baby or mother during labor/delivery",
      "Diagnoses such as HIE, brachial plexus injury, fractures, or severe lacerations",
      "OB, NICU, and operative records available",
      "No attorney currently handling this case",
      "Within applicable deadlines for minors/parents",
      "Family willing to discuss timeline and providers",
    ],
    overview: [
      "• What we review: Fetal monitoring strips, labor timelines, decision-to-incision intervals, cord gases, and neonatal outcomes.",
      "• Common issues: Delayed C-section, shoulder dystocia, mismanaged infections, or oxygen deprivation (HIE).",
      "• Long-term impact: Therapy, equipment, home modifications, and life-care planning may be needed and compensable.",
      "• Evidence gathering: We obtain hospital records and expert review where appropriate.",
      "• Family-centered approach: Sensitive intake and clear explanations—no pressure, no obligation.",
      "• Deadlines: Special rules can toll limitations for minors; we’ll explain your state’s specifics.",
      "• Contingency representation: No fees unless we recover.",
      "• Confidential review: Your privacy matters.",
    ],
    disqualifiers: [
      "No injury related to delivery",
      "No medical documentation",
    ],
    faqs: [
      {
        q: "Do we need all records first?",
        a: "No. Start the review; we’ll request what’s needed with your permission.",
      },
      {
        q: "Does a C-section mean malpractice?",
        a: "Not by itself—facts and timing matter. We’ll review the full picture.",
      },
      {
        q: "How long do cases take?",
        a: "Varies by complexity; we keep you updated.",
      },
      { q: "Any cost to start?", a: "No." },
    ],
  },

  {
    slug: "video-game-addiction",
    title: "Video Game Addiction Lawsuits",
    summary: "Compulsive use harms in youth.",
    heroImg: "/tort/video-game.jpg",
    highlights: ["Private review", "Family-focused support"],
    gallery: ["/tort/video-game1.jpg", "/tort/video-game2.jpg"],
    eligibility: [
      "Minor/young adult with harmful compulsive gaming",
      "Documented school decline, mood changes, or behavioral impacts",
      "Counseling/clinical notes helpful (not required to start)",
      "Parent/guardian involvement for minors",
      "No attorney currently handling this claim",
      "Incident within filing deadlines",
    ],
    overview: [
      "• Allegations: Design choices and platform practices may contribute to compulsive use and related harms in youth.",
      "• Evidence that helps: School reports, therapy notes, device logs, purchase histories, and parental statements.",
      "• Damages: Treatment costs, educational support, and non-economic harms can be considered based on the record.",
      "• Family support: We guide parents on documentation and next steps while respecting privacy.",
      "• Process: Intake → records → legal review → filing if appropriate → negotiation or litigation.",
      "• Timeliness: Early action preserves device/app data and counseling notes.",
      "• No upfront fees: Contingency only.",
      "• Confidential review: Sensitive issues handled with care.",
    ],
    disqualifiers: [
      "No documented impact",
      "No guardian involvement for minors",
    ],
    faqs: [
      {
        q: "Do we need a diagnosis first?",
        a: "Not required to start; we’ll review documented impacts and guidance from providers.",
      },
      {
        q: "Will this harm our child’s privacy?",
        a: "We protect identities and use secure channels for records.",
      },
      {
        q: "What if purchases were in-game?",
        a: "Transaction logs are useful—save receipts and screenshots.",
      },
      { q: "Any fees now?", a: "No." },
    ],
  },

  // ───────────────────────────────────────────────
  // 5) Physical & Sexual Abuse
  // ───────────────────────────────────────────────
  {
    slug: "lds-abuse",
    title: "LDS Mormon Church Abuse Lawsuit",
    summary: "Confidential claims for survivors.",
    heroImg: "/tort/lds3.jpg",
    highlights: ["Trauma-informed intake", "Confidential"],
    gallery: ["/tort/lds2.jpg", "/tort/lds.jpg"],
    eligibility: [
      "Survivor of abuse connected to LDS church context",
      "Occurred during childhood or adulthood",
      "Approximate location/timeframe available (city/state/years)",
      "Willing to share whether authorities or church were informed",
      "No attorney currently handling this matter",
      "Within applicable deadlines (varies by state and revival windows)",
    ],
    overview: [
      "• How we help: We offer trauma-informed intake, explain options, and connect survivors with attorneys experienced in faith-based abuse cases.",
      "• Understanding LDS abuse claims: Allegations include failure to report, intimidation, and internal handling of complaints rather than external accountability.",
      "• Evidence & documentation: Journals, messages, therapy notes, witness names, and any report numbers are helpful but not required to begin.",
      "• What damages may cover: Therapy costs, medical treatment, lost income, and non-economic harms; every survivor’s goals are respected.",
      "• Reporting questions: We can discuss whether formal reporting makes sense for you now and how it may impact your case.",
      "• Privacy: Your story is confidential. You choose what to share and when.",
      "• Time limits: Some states have revival windows or special rules; starting early preserves your options.",
      "• Dignity first: You’re in control of the pace and decisions.",
    ],
    disqualifiers: [
      "No abuse allegation",
      "Actively represented for the same matter",
    ],
    faqs: [
      {
        q: "Will my name be public?",
        a: "Intake is private. Your legal team can discuss confidentiality and protective orders.",
      },
      {
        q: "I never reported—can I still file?",
        a: "Yes. Many survivors disclose years later. We’ll evaluate under your state’s laws.",
      },
      {
        q: "Can therapy costs be included?",
        a: "Often yes—save receipts and provider details.",
      },
      {
        q: "Do I have to confront anyone?",
        a: "No. Most steps occur through counsel. You control participation.",
      },
    ],
  },

  {
    slug: "roblox-discord-child-abuse",
    title: "Roblox & Discord Child Abuse Lawsuit",
    summary: "Online platforms and child safety.",
    heroImg: "/tort/roblox4.jpg",
    highlights: ["Confidential review", "Guardian-led"],
    gallery: ["/tort/roblox2.jpg", "/tort/roblox3.jpg"],
    eligibility: [
      "Minor targeted via Roblox/Discord or related apps",
      "Harm: grooming, exploitation, harassment, or exposure to explicit material",
      "Parent/guardian contact to proceed",
      "Evidence/screens, reports, or chat records when available",
      "No attorney currently handling this claim",
      "Within applicable deadlines",
    ],
    overview: [
      "• What’s alleged: Platform design and moderation failures may allow grooming, extortion, or exposure to harmful content.",
      "• Evidence that helps: Chat logs, report confirmations, screenshots, witness statements, and therapist notes.",
      "• Damages: Counseling, education support, device replacement, and non-economic harms may be evaluated.",
      "• Reporting: We’ll discuss platform reporting and law-enforcement engagement where appropriate.",
      "• Guardian partnership: We guide families in a supportive, privacy-respecting process.",
      "• Timeliness: Preserve device data and accounts; do not delete messages without backups.",
      "• No upfront fees: Contingency-based representation.",
      "• Confidential review: Protects the child’s identity.",
    ],
    disqualifiers: [
      "No platform-related harm",
      "No guardian participation (for minors)",
    ],
    faqs: [
      {
        q: "We don’t have the chats—can we start?",
        a: "Yes. Begin intake and we’ll discuss recovery options for account data.",
      },
      {
        q: "Police were involved—still okay?",
        a: "Yes. Legal claims can proceed separately from criminal investigations.",
      },
      {
        q: "Will my child have to testify?",
        a: "Often not. We try to minimize direct involvement while still protecting rights.",
      },
      { q: "Upfront costs?", a: "None." },
    ],
  },

  {
    slug: "rideshare-assault",
    title: "Rideshare Abuse and Assault Lawsuits",
    summary: "Uber/Lyft passenger safety claims.",
    heroImg: "/tort/rideshare3.jpg",
    highlights: ["Confidential review", "No upfront fees"],
    gallery: ["/tort/rideshare1.jpg", "/tort/rideshare2.jpg"],
    eligibility: [
      "Incident occurred during a rideshare trip (passenger/driver)",
      "Assault, sexual assault, or severe harassment alleged",
      "Report to platform or authorities helpful (not required to start)",
      "Medical/therapy records or screenshots available",
      "No attorney handling this claim",
      "Within deadlines for your state",
    ],
    overview: [
      "• Case focus: Claims may involve negligent hiring, inadequate safety features, or failure to act on prior complaints.",
      "• Documentation: Trip receipts, in-app messages, report confirmations, medical/therapy notes, and witness names.",
      "• Damages: Treatment costs, lost income, and non-economic harms; each case is highly individual.",
      "• Reporting options: We’ll discuss platform and police reporting and what’s appropriate for you.",
      "• Privacy support: We keep your information confidential and work to minimize re-traumatization.",
      "• Timing: Earlier action preserves digital evidence and surveillance footage when available.",
      "• No-fee intake: Contingency representation only.",
      "• Clear next steps: Intake → records → legal evaluation → filing/negotiation.",
    ],
    disqualifiers: ["No incident tied to a rideshare trip"],
    faqs: [
      {
        q: "Do I have to sue the driver personally?",
        a: "Claims often involve the platform and other entities—facts determine targets.",
      },
      {
        q: "I never finished the ride—still eligible?",
        a: "Yes. Incidents before drop-off can still qualify.",
      },
      {
        q: "We didn’t report—can we still file?",
        a: "Yes. We’ll review evidence and discuss best next steps.",
      },
      { q: "Any cost to begin?", a: "No." },
    ],
  },

  {
  slug: "snapchat-addiction",
  title: "Snapchat Addiction Lawsuit",
  summary: "Mental health and social media addiction concerns linked to prolonged Snapchat use.",
  heroImg: "/tort/snapchat-addiction.jpg",

  highlights: [
    "Confidential case review",
    "No upfront costs"
  ],

  gallery: [
    "/tort/snapchat-addiction1.jpg",
    "/tort/snapchat-addiction2.jpg"
  ],

  eligibility: [
  "Snapchat was regularly used by the victim",
  "Online grooming, exploitation, abuse, or harmful contact occurred",
  "Mental health injuries or emotional trauma resulted",
  "Medical, therapy, school, or police records may be available",
  "Parents or guardians seeking legal review for a minor may qualify",
  "No attorney currently representing this claim"
],

  overview: [
  "• Snapchat has faced allegations that certain platform features may contribute to online exploitation, grooming, addiction, and harmful interactions involving minors.",
  "• Features such as disappearing messages, Quick Add, Snap Map, and algorithmic recommendations have been criticized in multiple lawsuits.",
  "• Families report emotional trauma, depression, anxiety, addiction-related behavior, exploitation, and long-term psychological harm.",
  "• Legal claims allege social media companies failed to implement sufficient safeguards to protect children and vulnerable users.",
  "• Evidence that may help support a claim includes chat history, police reports, therapy records, school records, screenshots, and account activity.",
  "• Compensation may include emotional distress damages, therapy expenses, medical costs, and other losses related to the abuse or grooming.",
  "• Attorneys are actively reviewing cases involving minors and young adults harmed through social media interactions.",
  "• Confidential legal consultations are available for qualifying individuals and families."
],

  disqualifiers: [
  "No Snapchat use",
  "No grooming, exploitation, or mental health harm",
  "Already represented by an attorney for this claim"
],

  faqs: [
  {
    q: "Who may qualify for a Snapchat lawsuit?",
    a: "Individuals or families alleging grooming, exploitation, addiction-related harm, or emotional injuries connected to Snapchat use may qualify for review."
  },
  {
    q: "Can parents file on behalf of a child?",
    a: "Yes. Parents or legal guardians may pursue claims involving minors."
  },
  {
    q: "What evidence may help support a claim?",
    a: "Screenshots, police reports, therapy records, school documentation, and account history may help support eligibility."
  },
  {
    q: "Does it cost anything upfront?",
    a: "No. Most mass tort attorneys handle these cases on a contingency basis."
  },
  {
    q: "Is my consultation confidential?",
    a: "Yes. Information submitted for review is typically handled confidentially."
    }
  ],
},
];
